import React, { useEffect, useMemo, useRef } from 'react';
import { Alert, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setDialog } from 'src/features/dialog/dialogSlice';
import {
  useGetTenantInformationQuery,
  useSendTenantTestMailMutation,
  useUpdateTenantMailSettingsMutation,
} from 'src/features/tenants/tenantsApiSlice';
import { selectCurrentUserData } from 'src/features/auth/authSlice';
import MailTemplatePageLayout from './MailTemplatePageLayout';

const tokens = [
  {
    value: '{{formalGreeting}}',
    label: 'Formale Anrede',
    desc: 'Automatische Anrede basierend auf dem Titel: Herr → Sehr geehrter Herr [Nachname], Frau → Sehr geehrte Frau [Nachname], kein Titel → Guten Tag [Vollständiger Name].',
  },
  {
    value: '{{memberFirstName}}',
    label: 'Vorname',
    desc: 'Vorname des Mitglieds.',
  },
  {
    value: '{{memberLastName}}',
    label: 'Nachname',
    desc: 'Nachname des Mitglieds.',
  },
  {
    value: '{{memberFullName}}',
    label: 'Vollständiger Name',
    desc: 'Vor- und Nachname des Mitglieds.',
  },
  {
    value: '{{contractOwner}}',
    label: 'Vertragsinhaber',
    desc: 'Name der Person, die den Vertrag abgeschlossen hat.',
  },
  {
    value: '{{contractEmail}}',
    label: 'E-Mail-Adresse',
    desc: 'Die im Vertrag hinterlegte E-Mail-Adresse.',
  },
  {
    value: '{{reason}}',
    label: 'Ablehnungsgrund',
    desc: 'Der vom Admin beim Ablehnen eingegebene Grund. Nur vorhanden, wenn ein Grund angegeben wurde.',
  },
  {
    value: '{{#hasReason}}…{{/hasReason}}',
    label: 'Block: mit Grund',
    desc: 'Dieser Textblock wird nur angezeigt, wenn der Admin beim Ablehnen einen Grund angegeben hat. Beispiel: {{#hasReason}}Begründung: {{reason}}{{/hasReason}}',
  },
  {
    value: '{{^hasReason}}…{{/hasReason}}',
    label: 'Block: ohne Grund',
    desc: 'Dieser Textblock wird nur angezeigt, wenn KEIN Ablehnungsgrund angegeben wurde. Nützlich für eine allgemeine Formulierung.',
  },
  {
    value: '{{tenantName}}',
    label: 'Vereinsname',
    desc: 'Name Ihres Vereins.',
  },
  {
    value: '{{today}}',
    label: 'Heutiges Datum',
    desc: 'Das aktuelle Datum zum Zeitpunkt des E-Mail-Versands.',
  },
];

const additionalInfo = (
  <Alert severity='info'>
    <Typography variant='body2' sx={{ mb: 1 }}>
      <strong>Bedingte Textblöcke (Mustache-Syntax):</strong>
    </Typography>
    <Typography variant='body2' component='div'>
      Mit <code>{`{{#hasReason}}…{{/hasReason}}`}</code> können Sie einen
      Abschnitt definieren, der nur erscheint, wenn der Admin beim Ablehnen
      einen Grund angegeben hat.
      <br />
      Mit <code>{`{{^hasReason}}…{{/hasReason}}`}</code> definieren Sie den
      Abschnitt, der erscheint, wenn <strong>kein</strong> Grund angegeben
      wurde.
      <br />
      Innerhalb des Blocks können Sie <code>{`{{reason}}`}</code> verwenden,
      um den Ablehnungsgrund einzufügen.
    </Typography>
  </Alert>
);

const MailSelfRegRejectionTemplateSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const currentUserData = useSelector(selectCurrentUserData);
  const { data: tenantInfo, isLoading } = useGetTenantInformationQuery();
  const [updateTenantMailSettings, { isLoading: isSaving }] =
    useUpdateTenantMailSettingsMutation();
  const [sendTenantTestMail, { isLoading: isTesting }] =
    useSendTenantTestMailMutation();

  const defaultValues = useMemo(
    () => ({
      selfRegRejectionSubject:
        tenantInfo?.settings?.mail?.selfRegRejectionSubject || 'Anmeldung abgelehnt',
      selfRegRejectionTemplate:
        tenantInfo?.settings?.mail?.selfRegRejectionTemplate ||
        '{{formalGreeting}},\n\nvielen Dank für Ihr Interesse an einer Mitgliedschaft bei {{tenantName}}.\n\nNach Prüfung Ihrer Anmeldung müssen wir Ihnen leider mitteilen, dass wir Ihre Anmeldung nicht genehmigen können.\n\n{{#hasReason}}Begründung:\n{{reason}}\n\n{{/hasReason}}{{^hasReason}}Bei Fragen stehen wir Ihnen gerne zur Verfügung.\n\n{{/hasReason}}Mit freundlichen Grüßen\n{{tenantName}}',
      selfRegRejectionTemplateMode:
        tenantInfo?.settings?.mail?.selfRegRejectionTemplateMode || 'auto',
      selfRegRejectionBcc: tenantInfo?.settings?.mail?.selfRegRejectionBcc || '',
      testRecipient: currentUserData?.mail || '',
    }),
    [tenantInfo, currentUserData]
  );

  const { register, handleSubmit, control, reset, setValue, getValues } =
    useForm({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleInsertToken = (token) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertText', false, token);
      const next = editorRef.current.innerHTML || '';
      setValue('selfRegRejectionTemplate', next, { shouldDirty: true });
      return;
    }
    const current = getValues('selfRegRejectionTemplate') || '';
    setValue(
      'selfRegRejectionTemplate',
      current ? `${current} ${token}` : token,
      { shouldDirty: true }
    );
  };

  const onSubmit = async (data) => {
    const { testRecipient, ...mailSettings } = data;
    try {
      const result = await updateTenantMailSettings(mailSettings).unwrap();
      if (result?.type === 'success') {
        dispatch(
          setDialog({
            title: 'Einstellungen gespeichert',
            type: 'SUCCESS',
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: 'Fehler',
          type: 'ERROR',
          data: {
            errorText:
              error?.data?.message ||
              'Einstellungen konnten nicht gespeichert werden.',
          },
        })
      );
    }
  };

  const handleSendTest = async () => {
    const recipient = (getValues('testRecipient') || '').trim();
    if (!recipient) {
      dispatch(
        setDialog({
          title: 'Hinweis',
          type: 'ERROR',
          data: { errorText: 'Bitte eine Test-E-Mail-Adresse angeben.' },
        })
      );
      return;
    }
    try {
      const result = await sendTenantTestMail({ recipient }).unwrap();
      if (result?.type === 'success') {
        dispatch(
          setDialog({
            title: 'Test-E-Mail gesendet',
            type: 'SUCCESS',
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: 'Fehler',
          type: 'ERROR',
          data: {
            errorText:
              error?.data?.message || 'Test-E-Mail konnte nicht gesendet werden.',
          },
        })
      );
    }
  };

  if (isLoading) return null;

  return (
    <MailTemplatePageLayout
      title='Selbstanmeldung — Ablehnung'
      description='Individualisieren Sie den Text der Ablehnungs-E-Mail, die bei Ablehnung einer Selbstanmeldung an das Mitglied versendet wird.'
      subjectFieldName='selfRegRejectionSubject'
      templateFieldName='selfRegRejectionTemplate'
      bccFieldName='selfRegRejectionBcc'
      templateType='selfRegRejection'
      modeFieldName='selfRegRejectionTemplateMode'
      currentHtmlTemplate={tenantInfo?.settings?.mail?.selfRegRejectionHtmlTemplate}
      tokens={tokens}
      additionalInfo={additionalInfo}
      control={control}
      register={register}
      handleInsertToken={handleInsertToken}
      editorRef={editorRef}
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      handleSendTest={handleSendTest}
      isSaving={isSaving}
      isTesting={isTesting}
      tenantName={tenantInfo?.tenantName}
      navigate={navigate}
    />
  );
};

export default MailSelfRegRejectionTemplateSettings;
